/**
 * LeaderboardSelfStandingBar — "your standing" bar pinned to the bottom of a
 * leaderboard (squad page and global leaderboard). Surfaces the
 * signed-in member's rank + points at a glance so low-ranked members in large
 * lists never have to scroll to find themselves. Tapping it jumps the list to
 * their row (scroll behaviour is owned by the parent).
 */

import React, { useMemo, useRef, useEffect } from 'react';
import { View, Text, Pressable, Platform, Animated, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { usePlayerRankLookup } from '../../../src/economy/contexts/PlayerRanksContext';
import { PlayerLevelPill } from '../../../src/economy/components/PlayerLevelPill/PlayerLevelPill';
import { LeaderboardUserAvatar } from './LeaderboardUserAvatar';
import { getWorldCupLeaderboardStyles } from './WorldCupLeaderboard.styles';
import { SELF_BAR_COPY, buildSelfBarA11yLabel, SELF_BAR_TEST_ID } from './WorldCupLeaderboard.constants';
import { getLeaderboardSelfStandingBarStyles } from './LeaderboardSelfStandingBar.styles';
import type { LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';

interface LeaderboardSelfStandingBarProps {
  entry: LeaderboardEntry;
  /** Jump the list to the member's row. */
  onPress: () => void;
  /**
   * When false, the bar fades + slides out (and stops capturing touches) instead
   * of rendering. Used by the global leaderboard to hide the bar while the
   * member's own row is on screen, so they never see themselves twice. Defaults
   * to true — callers that always want the bar (e.g. squad page) can omit it.
   */
  visible?: boolean;
}

export const LeaderboardSelfStandingBar: React.FC<LeaderboardSelfStandingBarProps> = ({
  entry,
  onPress,
  visible = true,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const baseStyles = useMemo(() => getWorldCupLeaderboardStyles(theme), [theme]);
  const styles = useMemo(
    () => getLeaderboardSelfStandingBarStyles(theme, { bottomInset: insets.bottom, windowWidth }),
    [theme, insets.bottom, windowWidth],
  );

  // Null unless a PlayerRanksProvider is mounted above (global WC leaderboard only).
  const playerRank = usePlayerRankLookup()(entry.userId);

  const reveal = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(reveal, {
      toValue: visible ? 1 : 0,
      duration: 280,
      useNativeDriver: true,
    }).start();
  }, [reveal, visible]);

  return (
    <View style={styles.wrap} pointerEvents="box-none">
      <Animated.View
        pointerEvents={visible ? 'auto' : 'none'}
        style={[
          styles.inner,
          {
            opacity: reveal,
            transform: [
              {
                translateY: reveal.interpolate({ inputRange: [0, 1], outputRange: [16, 0] }),
              },
            ],
          },
        ]}
      >
        <Pressable
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel={buildSelfBarA11yLabel(entry.rank, entry.points)}
          testID={SELF_BAR_TEST_ID}
          style={({ pressed }) => [
            styles.pressRoot,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.barPressed,
          ]}
        >
          {({ hovered }) => (
            <View style={[styles.bar, hovered && Platform.OS === 'web' && styles.barHover]}>
              <Text style={styles.rank} numberOfLines={1}>
                {SELF_BAR_COPY.RANK_PREFIX}
                {entry.rank}
              </Text>

              <LeaderboardUserAvatar
                entry={entry}
                variant="row"
                styles={baseStyles}
                showProfileBadge={false}
              />

              <Text style={styles.name} numberOfLines={1} ellipsizeMode="tail">
                {entry.name}
              </Text>

              <View style={styles.youChip}>
                <Text style={styles.youChipText}>{SELF_BAR_COPY.YOU}</Text>
              </View>

              {/* Inline beside the YOU chip, not under the name: this bar is a single-line
                  floating overlay, so a stacked pill would grow it (specs/rank-on-social-screens §7). */}
              {playerRank != null ? <PlayerLevelPill level={playerRank.level} /> : null}

              <Text style={styles.points}>
                {entry.points} {SELF_BAR_COPY.POINTS_LABEL}
              </Text>
            </View>
          )}
        </Pressable>
      </Animated.View>
    </View>
  );
};
